import React from 'react';
import { motion } from 'framer-motion';
import { X, Plus, Minus, ArrowLeft, ArrowRight } from 'lucide-react';

interface FullScreenImageProps {
  imageUrl: string;
  alt: string;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  hasMultipleImages?: boolean;
}

export function FullScreenImage({
  imageUrl,
  alt,
  onClose,
  onNext,
  onPrevious,
  hasMultipleImages = false
}: FullScreenImageProps) {
  const [scale, setScale] = React.useState(1);
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });

  // Función para manejar el zoom con la rueda del ratón
  const handleWheel = React.useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      // Zoom in
      setScale(prev => Math.min(prev + 0.1, 3));
    } else {
      // Zoom out
      setScale(prev => Math.max(prev - 0.1, 0.5));
    }
  }, []);

  // Función para manejar el doble clic para zoom
  const handleDoubleClick = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (scale > 1) {
      // Si ya está ampliado, volver al tamaño normal
      setScale(1);
      setPosition({ x: 0, y: 0 });
    } else {
      // Ampliar al hacer doble clic
      setScale(2);
    }
  }, [scale]);

  const handleZoomIn = React.useCallback(() => {
    setScale(prev => Math.min(prev + 0.25, 3));
  }, []);

  const handleZoomOut = React.useCallback(() => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  }, []);

  const handleMouseDown = React.useCallback((e: React.MouseEvent) => {
    if (scale > 1) {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  }, [scale, position]);

  const handleMouseMove = React.useCallback((e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      e.preventDefault();
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, scale, dragStart]);

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
  }, []);

  React.useEffect(() => {
    // Bloquear el scroll del body
    document.body.style.overflow = 'hidden';

    // Limpiar cuando el componente se desmonta
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  React.useEffect(() => {
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight' && onNext) {
        e.preventDefault();
        onNext();
      } else if (e.key === 'ArrowLeft' && onPrevious) {
        e.preventDefault();
        onPrevious();
      } else if (e.key === '+') {
        e.preventDefault();
        handleZoomIn();
      } else if (e.key === '-') {
        e.preventDefault();
        handleZoomOut();
      }
    };

    window.addEventListener('keydown', keyHandler);

    return () => {
      window.removeEventListener('keydown', keyHandler);
    };
  }, [onNext, onPrevious, onClose, handleZoomIn, handleZoomOut]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/95 backdrop-blur-lg z-[10000] flex items-center justify-center"
      onClick={onClose}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
    >
      {/* Controles superiores */}
      <div className="absolute top-4 right-4 flex items-center space-x-2">
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleZoomOut();
          }}
          className="p-2 rounded-full bg-zinc-800/80 text-white hover:bg-zinc-700/80 transition-colors"
          aria-label="Alejar"
          title="Alejar (tecla -)"
        >
          <Minus className="w-5 h-5" />
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleZoomIn();
          }}
          className="p-2 rounded-full bg-zinc-800/80 text-white hover:bg-zinc-700/80 transition-colors"
          aria-label="Acercar"
          title="Acercar (tecla +)"
        >
          <Plus className="w-5 h-5" />
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Restablecer zoom y posición
            setScale(1);
            setPosition({ x: 0, y: 0 });
          }}
          className={`p-2 rounded-full ${scale !== 1 ? 'bg-zinc-800/80 text-white hover:bg-zinc-700/80' : 'bg-zinc-800/40 text-white/50 cursor-not-allowed'} transition-colors`}
          aria-label="Restablecer zoom"
          title="Restablecer zoom"
          disabled={scale === 1}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
          </svg>
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          className="p-2 rounded-full bg-zinc-800/80 text-white hover:bg-zinc-700/80 transition-colors"
          aria-label="Cerrar"
          title="Cerrar (tecla Esc)"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div
        className="relative w-full h-full flex items-center justify-center overflow-hidden"
        onMouseMove={handleMouseMove}
      >
        <div
          className={`relative ${isDragging ? 'cursor-grabbing' : scale > 1 ? 'cursor-grab' : 'cursor-zoom-in'}`}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={handleMouseDown}
          onDoubleClick={handleDoubleClick}
        >
          <img
            src={imageUrl}
            alt={alt || "Imagen a pantalla completa"}
            className="max-h-[85vh] max-w-[85vw] object-contain select-none p-4"
            style={{
              transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
              transition: isDragging ? 'none' : 'transform 0.2s'
            }}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = "/placeholder-image.png";
            }}
            draggable={false}
          />
        </div>

        {/* Indicador de nivel de zoom */}
        {scale !== 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-zinc-800/80 text-white px-3 py-1 rounded-full text-sm">
            Zoom: {Math.round(scale * 100)}%
          </div>
        )}
      </div>

      {/* Controles de navegación */}
      {hasMultipleImages && (
        <>
          {onPrevious && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onPrevious();
              }}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-zinc-800/80 text-white hover:bg-zinc-700/80 transition-colors z-10"
              aria-label="Imagen anterior"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
          )}

          {onNext && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onNext();
              }}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-zinc-800/80 text-white hover:bg-zinc-700/80 transition-colors z-10"
              aria-label="Imagen siguiente"
            >
              <ArrowRight className="w-6 h-6" />
            </button>
          )}
        </>
      )}

      {/* Instrucciones */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-zinc-800/80 px-4 py-2 rounded-full text-sm text-white/80">
        {hasMultipleImages ? 'Usa ← → para navegar • +/- para zoom • ESC para cerrar' : '+/- para zoom • ESC para cerrar'}
      </div>
    </motion.div>
  );
}
